import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { presetAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Send, Loader2, Sparkles, Check, Save, Globe, FileText,
  Target, Mic2, PenTool, RefreshCcw, Bot, User
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function PresetBuilderPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [draftPreset, setDraftPreset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Initial AI greeting
    setMessages([{
      role: 'assistant',
      content: "Hey! I'm here to help you build a custom content generation preset. I'll guide you through the process step by step.\n\nTo get started, tell me: **What kind of content do you create, and who is your audience?**\n\nFor example: \"I run a fitness YouTube channel targeting 25-35 year old gym-goers\" or \"I host a tech podcast for startup founders\".",
    }]);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setSending(true);

    try {
      const chatHistory = newMessages.map(m => ({ role: m.role, content: m.content }));
      const res = await presetAPI.chat(userMsg, chatHistory, draftPreset);

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);

      if (res.data.has_preset && res.data.preset_data) {
        setDraftPreset(res.data.preset_data);
        setEditedPrompt(res.data.preset_data.systemPrompt || '');
        setShowReview(true);
      }
    } catch (err) {
      toast.error('Failed to get AI response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble processing that. Could you try rephrasing?',
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleSavePreset = async (publish) => {
    if (!draftPreset) return;
    setSaving(true);
    try {
      const payload = {
        name: draftPreset.name,
        description: draftPreset.description,
        targetPlatforms: draftPreset.targetPlatforms || [],
        vertical: draftPreset.vertical || '',
        tone: draftPreset.tone || '',
        systemPrompt: editingPrompt ? editedPrompt : (draftPreset.systemPrompt || ''),
        status: 'published',
      };
      await presetAPI.create(payload);
      setShowSuccess(true);
      setTimeout(() => navigate('/presets'), 2500);
    } catch (err) {
      toast.error('Failed to save preset');
    } finally {
      setSaving(false);
    }
  };

  const handleRefine = () => {
    setShowReview(false);
    setInput('I\'d like to adjust the preset. ');
    inputRef.current?.focus();
  };

  const renderMarkdown = (text) => {
    // Simple markdown: **bold**, `code`, and newlines
    return text.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**'))
          return <strong key={j} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`'))
          return <code key={j} className="text-[11px] bg-white/5 px-1.5 py-0.5 rounded font-mono text-primary">{part.slice(1, -1)}</code>;
        return part;
      });
      return <span key={i}>{parts}{i < text.split('\n').length - 1 && <br />}</span>;
    });
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="preset-builder-page">
      {/* Success Animation Overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm animate-in fade-in duration-300" data-testid="preset-success-overlay">
          <div className="flex flex-col items-center gap-4 animate-in zoom-in-75 duration-500">
            <div className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-12 h-12 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" className="animate-[draw_0.5s_ease-in-out_0.3s_forwards]" style={{ strokeDasharray: 30, strokeDashoffset: 30, animation: 'draw 0.5s ease-in-out 0.3s forwards' }} />
              </svg>
            </div>
            <h3 className="font-heading text-2xl font-bold">Preset Published!</h3>
            <p className="text-sm text-muted-foreground">Your preset is now live in the Community Library</p>
          </div>
          <style>{`@keyframes draw { to { stroke-dashoffset: 0; } }`}</style>
        </div>
      )}
      {/* ─── Chat Panel ─── */}
      <div className={cn('flex flex-col transition-all duration-300', draftPreset && showReview ? 'flex-1' : 'flex-1 max-w-4xl mx-auto')}>
        {/* Header */}
        <div className="px-8 py-5 border-b border-border/30 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/presets')} data-testid="back-to-presets">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="font-heading text-lg font-semibold">AI Preset Builder</h2>
              <p className="text-xs text-muted-foreground">Describe your content style and I'll build a preset for you</p>
            </div>
          </div>
          {draftPreset && (
            <Button
              onClick={() => setShowReview(true)}
              variant="secondary"
              size="sm"
              className="rounded-full"
              data-testid="review-preset-button"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Review Preset
            </Button>
          )}
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 px-8" ref={scrollRef}>
          <div className="py-6 space-y-6 max-w-3xl mx-auto" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn('flex gap-3', msg.role === 'user' ? 'justify-end' : 'justify-start')}
                data-testid={`chat-message-${i}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  </div>
                )}
                <div className={cn(
                  'rounded-2xl px-4 py-3 max-w-[80%] text-sm leading-relaxed',
                  msg.role === 'user'
                    ? 'bg-primary/15 text-foreground border border-primary/20'
                    : 'bg-secondary/40 text-foreground/90 border border-white/5'
                )}>
                  {renderMarkdown(msg.content)}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0 mt-1">
                    <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-3" data-testid="typing-indicator">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center shrink-0">
                  <Bot className="w-4 h-4 text-primary" strokeWidth={1.5} />
                </div>
                <div className="bg-secondary/40 border border-white/5 rounded-2xl px-4 py-3">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Preset ready banner */}
            {draftPreset && !showReview && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 flex items-center justify-between" data-testid="preset-ready-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Preset Ready for Review</p>
                    <p className="text-xs text-muted-foreground">{draftPreset.name}</p>
                  </div>
                </div>
                <Button onClick={() => setShowReview(true)} size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="open-review-button">
                  Review & Save
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="px-8 py-4 border-t border-border/30 shrink-0">
          <div className="flex gap-3 max-w-3xl mx-auto">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={draftPreset ? "Ask to refine the preset, or say 'looks good'..." : "Describe your content and audience..."}
              className="bg-secondary/30 border-white/5 focus:border-primary h-11"
              disabled={sending}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              size="icon"
              className="h-11 w-11 rounded-xl bg-primary hover:bg-primary/90 shrink-0"
              data-testid="send-message-button"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Review Panel ─── */}
      {draftPreset && showReview && (
        <div className="w-[480px] shrink-0 border-l border-border/30 bg-zinc-950/60 flex flex-col" data-testid="preset-review-panel">
          <div className="px-6 py-5 border-b border-border/30 flex items-center justify-between shrink-0">
            <h3 className="font-heading text-lg font-semibold">Review Preset</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowReview(false)} className="text-xs">
              Close
            </Button>
          </div>

          <ScrollArea className="flex-1 px-6 py-6">
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3 h-3" /> Name
                </label>
                <p className="font-heading font-semibold text-base">{draftPreset.name}</p>
              </div>

              {/* Description */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5 mb-1.5">
                  <FileText className="w-3 h-3" /> Description
                </label>
                <p className="text-sm text-foreground/80 leading-relaxed">{draftPreset.description}</p>
              </div>

              {/* Platforms */}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5 mb-1.5">
                  <Target className="w-3 h-3" /> Platforms
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {draftPreset.targetPlatforms?.map((p, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] bg-indigo-500/10 text-indigo-400 border-0">{p}</Badge>
                  ))}
                </div>
              </div>

              {/* Vertical & Tone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5 block">Vertical</label>
                  <Badge variant="secondary" className="text-[10px] bg-emerald-500/10 text-emerald-400 border-0">{draftPreset.vertical}</Badge>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5 mb-1.5">
                    <Mic2 className="w-3 h-3" /> Tone
                  </label>
                  <Badge variant="secondary" className="text-[10px] bg-purple-500/10 text-purple-400 border-0">{draftPreset.tone}</Badge>
                </div>
              </div>

              {/* System Prompt */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1.5">
                    <PenTool className="w-3 h-3" /> System Prompt
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => { setEditingPrompt(!editingPrompt); setEditedPrompt(draftPreset.systemPrompt || ''); }}
                    data-testid="toggle-edit-prompt"
                  >
                    {editingPrompt ? 'Preview' : 'Edit'}
                  </Button>
                </div>
                {editingPrompt ? (
                  <Textarea
                    value={editedPrompt}
                    onChange={(e) => setEditedPrompt(e.target.value)}
                    className="min-h-[200px] bg-secondary/30 border-white/5 focus:border-primary text-xs font-mono leading-relaxed resize-y"
                    data-testid="system-prompt-editor"
                  />
                ) : (
                  <div className="bg-secondary/20 border border-white/5 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                    <pre className="text-xs text-foreground/70 whitespace-pre-wrap font-mono leading-relaxed" data-testid="system-prompt-preview">
                      {draftPreset.systemPrompt?.slice(0, 500)}{draftPreset.systemPrompt?.length > 500 ? '...' : ''}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>

          {/* Action buttons */}
          <div className="px-6 py-4 border-t border-border/30 space-y-2 shrink-0">
            <Button
              onClick={handleRefine}
              variant="ghost"
              size="sm"
              className="w-full rounded-full"
              data-testid="refine-preset-button"
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refine with AI
            </Button>
            <Button
              onClick={() => handleSavePreset(true)}
              disabled={saving}
              className="w-full rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-indigo-500/25"
              data-testid="publish-preset-button"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Globe className="w-3.5 h-3.5 mr-1" />}
              Save & Publish to Community
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
